import React, { useRef } from 'react';
import { Button, Col, Input, Row } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import PropTypes from 'prop-types';

import { manualCall, removeCrmDetails } from '../../../store/slices/callingSlice';
import {
  setCallDialSec,
  setCallId,
  setCallNumber,
  setCurrentCallDetail,
  setScreenType,
  setSelectBreakType
} from '../../../store/slices/dialSlice';
import { useUserDetails } from '../../../store/slices/authSlice';
import { useCallingData } from '../../Context/CallingContext';
import { isOnBreakNow } from '../../../utils/breakState';
import { fetchCrmAndLand } from '../../../utils/crmLanding';

const DialPadScreen = ({ duration, number, setNumber, onIdleSet }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const timeoutRef = useRef(null);
  const user = useUserDetails();
  const { handleTimeReset } = useCallingData();

  // Default timeout duration, in seconds
  const timeoutDuration = Number(user?.process_details?.autoOutbound) || 5;

  const resetTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      dispatch(setSelectBreakType(null));
      onIdleSet();
      handleTimeReset();
      setNumber('');
    }, timeoutDuration * 1000);
  };

  const onNumberChange = (val) => {
    resetTimer();
    if (number?.length < val.length && number?.length === 0) {
      dispatch(setSelectBreakType({ name: 'OUTGOING', color: 'warning' }));
      handleTimeReset();
    } else if (number?.length > val.length && val.length === 0) {
      clearTimeout(timeoutRef.current);
      dispatch(setSelectBreakType(null));
      onIdleSet();
      handleTimeReset();
    }

    if (number?.length < 20) {
      setNumber(number + val);
    }
  };

  const dialNumber = async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // The dial pad is the default screen during a break, so it stays reachable in
    // the window before the break modal mounts. Never originate a call from it then.
    if (isOnBreakNow()) return;
    try {
      const payload = {
        destination_number: number
      };
      const response = await dispatch(manualCall(payload)).unwrap();
      if (response && response?.data) {
        // A manual dial is always a fresh call — clear the prior call's CRM details
        // so the landing recovery reads "no details yet" for this call.
        dispatch(removeCrmDetails());
        dispatch(setCallDialSec(duration));
        dispatch(setCallId(response?.data?.origination_uuid));
        dispatch(setCurrentCallDetail(response?.data));
        // When embedded in an iframe, show the destination number returned by the
        // manual call response in the live call display.
        if (window.self !== window.top && response?.data?.destination_number) {
          setNumber(response.data.destination_number);
          dispatch(setCallNumber(response.data.destination_number));
        }
        // Shared landing implementation — retries, break guards, and the embedded
        // route split all live there.
        fetchCrmAndLand({ dispatch, navigate, data: response?.data });
        dispatch(setScreenType('LIVE_CALL'));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleRemoveIconClick = () => {
    const num = number.slice(0, -1) || '';
    resetTimer();

    if (number?.length > 0 && num.length === 0) {
      clearTimeout(timeoutRef.current);
      dispatch(setSelectBreakType(null));
      onIdleSet();
      handleTimeReset();
    }
    setNumber(num);
  };

  return (
    <div className="input-container">
      <div className="input-wrapper">
        <Input
          type="text"
          value={number}
          onChange={(e) => {
            const value = e.target.value;
            const val = value.replace(/[^0-9*#]+/g, '');
            resetTimer();
            if (number?.length < val.length && number?.length === 0) {
              dispatch(setSelectBreakType({ name: 'OUTGOING', color: 'warning' }));
              handleTimeReset();
            } else if (number?.length > val.length && val.length === 0) {
              clearTimeout(timeoutRef.current);
              dispatch(setSelectBreakType(null));
              onIdleSet();
              handleTimeReset();
            }
            if (val?.length < 20) setNumber(val);
          }}
          className="dail-input"
          pattern="[0-9]+"
        />
        <div className="remove-icon-wrapper" onClick={handleRemoveIconClick}>
          <Icon.Delete className="remove-icon" />
        </div>
      </div>

      <Row className="button-grid pb-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((val, idx) => (
          <Col key={idx} className="button-col p-0">
            <div className="dial-button" onClick={() => onNumberChange(val)}>
              <span>{val}</span>
            </div>
          </Col>
        ))}
        <div></div>
        <div className="call-button-wrapper">
          <Button className="call-button" onClick={() => dialNumber()}>
            <Icon.PhoneCall className="call-icon" />
          </Button>
        </div>
      </Row>
    </div>
  );
};

DialPadScreen.propTypes = {
  number: PropTypes.string,
  setNumber: PropTypes.func,
  onIdleSet: PropTypes.func,
  duration: PropTypes.any
};

export default DialPadScreen;
